/**
 * AdminModulePricing (T8.1a F5) — admin pricing management page.
 *
 * Container: fetches modules (full mode, include_inactive) + pricing in parallel.
 * Passes data to PricingGrid for display + edit-on-blur.
 */

import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { Layout } from '@/components/Layout';
import { PricingGrid } from '@/components/PricingGrid';
import { Skeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';

interface ModuleListItem {
  id: string;
  code: string;
  name: string;
  isCore: boolean | null;
  isActive: boolean | null;
  pricingCount: number;
  subscribedTenantCount: number;
  gcrecord: string | null;
  areaId: string | null;
  areaName: string | null;
  areaSortOrder: number | null;
}

interface ModulesResponse {
  modules: ModuleListItem[];
  total: number;
}

interface PricingRow {
  id: string;
  moduleId: string;
  pricingModel: string;
  priceMonthly: number;
  priceYearly: number | null;
  currencyCode: string;
  isActive: boolean | null;
}

interface PricingResponse {
  pricing: PricingRow[];
}

export function AdminModulePricing() {
  const { t } = useTranslation();

  const [modulesQuery, pricingQuery] = useQueries({
    queries: [
      {
        queryKey: ['admin', 'modules', 'list'],
        queryFn: async (): Promise<ModulesResponse> => {
          const res = await api.get<ModulesResponse>(
            '/admin/modules?detail=full&include_inactive=true',
          );
          return res.data;
        },
      },
      {
        queryKey: ['admin', 'module-pricing'],
        queryFn: async (): Promise<PricingResponse> => {
          const res = await api.get<PricingResponse>('/admin/module-pricing');
          return res.data;
        },
      },
    ],
  });

  const isLoading = modulesQuery.isLoading || pricingQuery.isLoading;
  const isError = modulesQuery.isError || pricingQuery.isError;

  return (
    <Layout title={t('admin.modulePricing.title')}>
      <section className="px-6 py-8 flex flex-col gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-text-primary">
            {t('admin.modulePricing.title')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.modulePricing.subtitle')}</p>
        </div>

        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={64} />
            ))}
          </div>
        ) : isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            <p>{t('admin.modulePricing.loadError')}</p>
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => {
                void modulesQuery.refetch();
                void pricingQuery.refetch();
              }}
            >
              {t('admin.modulePricing.retry')}
            </Button>
          </div>
        ) : (
          <>
            <div
              role="status"
              className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
            >
              {t('admin.modulePricing.retiredHiddenBanner')}
            </div>
            <PricingGrid
              modules={(modulesQuery.data?.modules ?? [])
                .filter((m) => m.gcrecord === null)
                .map((m) => ({
                  id: m.id,
                  code: m.code,
                  name: m.name,
                  areaId: m.areaId,
                  areaName: m.areaName,
                  areaSortOrder: m.areaSortOrder,
                }))}
              pricing={pricingQuery.data?.pricing ?? []}
            />
          </>
        )}
      </section>
    </Layout>
  );
}
